/* eslint-disable react-native/no-inline-styles */
/**
 * ChangePassword Screen
 * 
 * Allows authenticated users to change their password
 * Features:
 * - Password strength indicator (visual bar)
 * - Password visibility toggle
 * - Validation: min 6 chars, 1 uppercase, 1 number, 1 special char
 * - Real-time strength calculation (0-100%)
 * - Color-coded strength levels (red/orange/yellow/green)
 * - Logs user out after successful password change
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 */
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Text, TextInput } from 'react-native-paper';
import { theme } from '../theme/theme';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik/dist';
import * as Yup from 'yup';
import { doPut } from '../services/http';
import { errorHandler, logoutHandler } from '../services/Utils';
import { showLoader, showToast } from '../store/info.slice';

const InputRoundness = 6;
// Regex for password strength
const passwordValidationRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;

// Validation schema using Yup
const validationSchema = Yup.object().shape({
    password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .matches(passwordValidationRegex, 'Password must contain at least one uppercase letter, one number, and one special character')
        .required('Password is required'),
});


/**
 * Main component for changing the user's password.
 * Handles password validation, strength checking, and submission.
 * @param {object} navigation - Navigation prop for screen navigation
 */
function ChangePassword({ navigation }) {
    const dispatch = useDispatch();
    const [secureTextEntry, setSecureTextEntry] = useState(true);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);

    // Function to calculate password strength and return percentage
    /**
     * Calculates the password strength as a percentage.
     * @param {string} password - The password to check
     * @returns {number} Strength percentage (0-100)
     */
    const getPasswordStrength = (password) => {
        let strength = 0;

        // Check length (minimum 6 characters)
        if (password.length >= 6) { strength += 20; }

        // Check for uppercase letters
        if (password.match(/[A-Z]/)) { strength += 20; }

        // Check for numbers
        if (password.match(/\d/)) { strength += 20; }

        // Check for special characters
        if (password.match(/[@$!%*?&]/)) { strength += 20; }

        // Check for mixed case
        if (password.match(/[a-z]/) && password.match(/[A-Z]/)) { strength += 20; }

        return strength;
    };

    // Function to get the strength label and color
    /**
     * Returns a label and color based on password strength.
     * @param {number} strength - Password strength percentage
     * @returns {{label: string, color: string}}
     */
    const getStrengthLabelAndColor = (strength) => {
        if (strength < 40) {
            return { label: 'Weak', color: 'red' };
        } else if (strength < 70) {
            return { label: 'Medium', color: 'orange' };
        } else {
            return { label: 'Strong', color: 'green' };
        }
    };

    // Toggle password visibility
    /**
     * Toggles the visibility of the password input field.
     */
    const togglePasswordVisibility = () => {
        setSecureTextEntry((prevState) => !prevState);
    };

    /**
     * Handles form submission for changing the password.
     * @param {object} param0 - Contains values, resetForm, setSubmitting
     */
    const onSubmitHandler = ({ values, resetForm, setSubmitting }) => {
        dispatch(showLoader());
        doPut('/password/change', { password: values?.password }).then(response => {
            setSubmitting(false);
            resetForm();
            dispatch(showToast(response.data.message));
            logoutHandler();
        }).catch(error => {
            errorHandler(error);
            setSubmitting(false);
        });
    };


    return (
        <SafeAreaView edges={['bottom']} style={[styles.container, { backgroundColor: theme.colors.primary }]}>
            {/* <IconButton icon="arrow-left" size={24} iconColor="#ffffff" onPress={() => navigation.goBack()} /> */}
            {/* <View style={{ padding: 8, flexDirection: 'row', alignItems: 'center' }}>
                <View style={{ marginLeft: 16, flex: 1 }}>
                    <Text variant="titleLargeBold" style={{ color: '#ffffff' }}>Change Password</Text>
                    <Text variant="textLight" numberOfLines={2} style={{ color: '#ffffff' }}>Set a new password for your account. Make sure it’s strong and secure.</Text>
                </View>
            </View> */}
            <View style={styles.mainContainer}>
                <ScrollView contentContainerStyle={styles.KeyboardAwareScrollView}>
                    <Formik
                        initialValues={{ password: '' }}
                        validationSchema={validationSchema}
                        onSubmit={(values, { resetForm, setSubmitting }) => {
                            console.log('Form submitted with:', values);
                            onSubmitHandler({ values, resetForm, setSubmitting });
                        }}
                    >
                        {({ handleChange, handleBlur, handleSubmit, isSubmitting, values, errors, touched }) => {
                            const strength = getPasswordStrength(values.password);
                            const { label, color } = getStrengthLabelAndColor(strength);
                            return (
                                <View>
                                    <View style={styles.inputContainer}>
                                        <Text variant="titleMedium">New Password</Text>
                                        <TextInput
                                            placeholder="Enter Password"
                                            mode="outlined"
                                            theme={{ roundness: InputRoundness }}
                                            style={styles.input}
                                            contentStyle={styles.contentStyle}
                                            secureTextEntry={secureTextEntry}
                                            value={values.password}
                                            onChangeText={handleChange('password')}
                                            onBlur={handleBlur('password')}
                                            onFocus={() => setIsPasswordFocused(true)}
                                            maxLength={36}
                                            right={<TextInput.Icon color={theme.colors.primary} onPress={togglePasswordVisibility} size={24} icon={secureTextEntry ? 'eye-off' : 'eye'} />}

                                        />
                                        {touched.password && errors.password && (
                                            <Text style={styles.errorText}>{errors.password}</Text>
                                        )}
                                    </View>
                                    {/* Password Strength Bar (Only when password field is focused) */}
                                    {isPasswordFocused && values.password.length > 0 && (
                                        <View style={styles.passwordStrengthContainer}>
                                            <View
                                                style={[
                                                    styles.passwordStrengthBar,
                                                    { width: `${strength}%`, backgroundColor: color },
                                                ]}
                                            />
                                        </View>
                                    )}
                                    {isPasswordFocused && values.password.length > 0 && (
                                        <Text style={{ color, marginTop: 4, fontSize: 12 }}>
                                            Password Strength: {label}
                                        </Text>
                                    )}
                                    <View style={styles.inputContainer}>
                                        <Button
                                            theme={{ roundness: 36 }}
                                            onPress={handleSubmit}
                                            mode="contained"
                                            labelStyle={styles.labelStyle}
                                            disabled={isSubmitting}
                                        >
                                            Change Password
                                        </Button>
                                    </View>
                                </View>
                            )
                        }}
                    </Formik>
                </ScrollView>
            </View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        // backgroundColor: theme.colors.primary,
    },
    mainContainer: {
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 16,
        margin: 8,
    },
    KeyboardAwareScrollView: {
        flexGrow: 1,
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 8,
    },
    input: {
        fontSize: 14,
        backgroundColor: '#f6f8fa',
        marginVertical: 4,
    },
    contentStyle: {
        paddingLeft: 16,
    },
    inputContainer: {
        marginVertical: 8,
    },
    labelStyle: {
        fontSize: 16,
        letterSpacing: 2,
        paddingVertical: 4,
    },
    errorText: {
        color: 'red',
        fontSize: 12,
        marginTop: 4,
    },
    passwordStrengthContainer: {
        marginTop: 8,
        height: 4,
        backgroundColor: '#e0e0e0',
        borderRadius: 5,
    },
    passwordStrengthBar: {
        height: '100%',
        borderRadius: 5,
    },
});

export default ChangePassword;
